<script>
import ComposerInlineSelect from '@modules/aawz-intelligence-v2/components/composer-inline-select/composer-inline-select.vue'

// Largura do painel de agentes: cada item carrega ícone + nome + descrição, e
// o painel de 180px do seletor de modelo quebrava a legenda em três linhas.
const LARGURA_PAINEL_AGENTES = 320

const ICONE_PADRAO_DO_AGENTE = 'robot'

export default {
  name: 'IntelligenceV2AgentSelect',
  components: { ComposerInlineSelect },
  props: {
    // Gatilho sem rótulo nem chevron — o composer do modo limpo.
    soIcone: {
      type: Boolean,
      default: false,
    },
    // Motivo da trava, para o tooltip. Vazio quando o seletor está livre.
    motivoDaTrava: {
      type: String,
      default: '',
    },
    // Já FILTRADO por feature flag pelo pai (`agentesVisiveis($access)`) — este
    // componente não lê `$access`, para continuar puro e testável sem store.
    agentes: {
      type: Array,
      default: function () {
        return []
      },
    },
    agenteAtivoId: {
      type: String,
      default: '',
    },
    // Repassados ao dropdown. Os defaults sao os do composer, que e onde este
    // seletor vive na maior parte do tempo; o historico do popup mostra o chip
    // no TOPO e encostado a direita, entao inverte os dois.
    direction: {
      type: String,
      default: 'up',
    },
    align: {
      type: String,
      default: 'left',
    },
    // Turno em andamento: trocar de agente no meio do stream descartaria a
    // conversa em curso (§8.3).
    disabled: {
      type: Boolean,
      default: false,
    },
    // Na `/ia` o chip não abre dropdown: emite para o composer, que abre o
    // palette com a lista de agentes. Opt-in — popup e rota cheia não mudam.
    abrirPorFora: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Rótulo do tooltip no modo só-ícone: o nome do agente e, se a conversa
    // travou o seletor, o motivo — que numa fileira de 52px não caberia como
    // frase ao lado dos controles.
    tooltipDoChip() {
      if (!this.motivoDaTrava) return this.rotuloChip
      return this.rotuloChip + ' · ' + this.motivoDaTrava
    },
    larguraPainel() {
      return LARGURA_PAINEL_AGENTES
    },
    agenteAtivo() {
      const ativo = this.agentes.find(
        (agente) => agente.id === this.agenteAtivoId
      )
      return ativo || this.agentes[0] || null
    },
    // O chip mostra o nome curto; o nome longo só aparece dentro do menu.
    rotuloChip() {
      return this.agenteAtivo ? this.agenteAtivo.shortName : ''
    },
    // Especialistas do backend não trazem ícone: sem fallback o chip só-ícone
    // ficava um círculo vazio.
    iconeChip() {
      const icone = this.agenteAtivo && this.agenteAtivo.iconName
      return icone || ICONE_PADRAO_DO_AGENTE
    },
    // Um único agente visível: o chip continua na tela (mostra com quem se
    // fala), mas não há escolha a oferecer — abrir um menu de um item só é
    // ruído (§8.2). `disabled` do ComposerInlineSelect já bloqueia o toggle.
    agenteUnico() {
      return this.agentes.length < 2
    },
    menuBloqueado() {
      return this.disabled || this.agenteUnico
    },
    // Sem escolha a fazer NÃO é o mesmo que bloqueado por streaming: o
    // primeiro fica legível, o segundo esmaece para dizer "agora não".
    classeChip() {
      return { 'agent-select--sem-escolha': this.agenteUnico && !this.disabled }
    },
    opcoes() {
      const ativoId = this.agenteAtivoId
      return this.agentes.map((agente) => ({
        label: agente.label,
        description: agente.description,
        icon: agente.iconName,
        active: agente.id === ativoId,
        onClick: () => this.$emit('change', agente.id),
      }))
    },
    temAgente() {
      return Boolean(this.agenteAtivo)
    },
  },
  methods: {
    // Repassa para o dropdown, como o `composer-ferramentas` faz: o composer
    // abre este menu pelo comando `/agente` e não deve alcançar o filho por
    // dentro. Sem `comFoco` porque quem chama pelo comando quer NAVEGAR aqui.
    abrir() {
      const select = this.$refs.select
      if (select) select.abrir(true)
    },
    // Lido pelo `algumMenuAberto` do composer.
    isOpen() {
      const select = this.$refs.select
      return Boolean(select && select.isOpen())
    },
  },
}
</script>

<template>
  <!-- active-color é a cor da marca do ESCRITÓRIO (--primary), a mesma que a
       sidebar usa no agente ativo — a plataforma é white-label e verde fixo não
       sobrevive a escritório com identidade própria. Ele pinta o ÍCONE e o check
       DENTRO do menu (fundo branco): medido, --primary dá 4.35, acima do mínimo
       de 3:1 da WCAG 1.4.11 para elemento não-textual.

       ⚠️ `icon-color` explícito para o GATILHO ficar neutro, e só ele. Sem a
       prop, o `triggerIconColor` pinta o ícone de `activeColor` sempre que
       alguma opção está ativa — e aqui SEMPRE está: `agenteAtivo` cai no
       primeiro do catálogo quando nada casa. Um destaque que nunca varia não
       informa nada; só compete com o que varia de fato.

       É o oposto do chip de CLIENTE e do de MODELO, onde "nenhum" é um estado
       real e a cor separa os dois. Mesma escolha do "+"
       (`composer-ferramentas`), pelo mesmo motivo. -->
  <ComposerInlineSelect
    v-if="temAgente"
    ref="select"
    :abrir-por-fora="abrirPorFora"
    class="agent-select"
    :class="[classeChip, { 'agent-select--so-icone': soIcone }]"
    :align="align"
    :direction="direction"
    active-color="var(--primary)"
    icon-color="var(--neutral-700)"
    :label="rotuloChip"
    :icon="iconeChip"
    :so-icone="soIcone"
    :tooltip="tooltipDoChip"
    :options="opcoes"
    :panel-width="larguraPainel"
    :disabled="menuBloqueado"
    @abrir-externo="$emit('abrir-externo')"
  />
</template>

<style lang="sass" scoped>
// ⚠️ Preenchimento NEUTRO, não a cor de marca: a plataforma é white-label, e
// chip com cor fixa não sobrevive a escritório com identidade própria. Sobrepõe
// o §8.1, que pedia "chip verde" — quem carrega a cor é o ÍCONE (--primary,
// medido em 4.35 sobre branco, acima do mínimo de 3:1 da WCAG 1.4.11).
// Só-ícone: círculo de 30px, o mesmo molde do "+" e do enviar, para os três
// ficarem na mesma régua na fileira única.
.agent-select.agent-select--so-icone ::v-deep .composer-inline-select__trigger
  // ★ 34px e não os 30 do composer padrão: nesta caixa de 52px os botões
  // ficavam pequenos demais para a altura da linha. Vale para os QUATRO —
  // "+", agente, escopo e enviar — senão a fileira fica desalinhada.
  // Mudou aqui, mude nos outros quatro (`grep -rn "34px e não os 30"`).
  width: 34px
  height: 34px
  padding: 0
  justify-content: center
  border-radius: 50%
  // Sem preenchimento em repouso: com o cinza, os dois ícones liam como o botão
  // de enviar e a fileira virava três alvos de mesmo peso. O realce só no
  // hover, que é quando o cursor já os elegeu.
  background: transparent

.agent-select ::v-deep .composer-inline-select__trigger
  // Mesma receita nos três chips do composer — agente, modelo e escopo.
  // Mudou aqui, mude nos outros dois (`grep -rn "chip do composer"`).
  border-radius: 999px
  padding: 4px 10px
  background: var(--neutral-100)
  color: var(--neutral-900)

  &:hover,
  &.composer-inline-select__trigger--open
    background: var(--neutral-200)

.agent-select--sem-escolha ::v-deep .composer-inline-select__trigger:disabled
  // Um único agente não é erro: o chip fica legível, só não abre.
  opacity: 1
  cursor: default
</style>
